import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import Paginator from '~/Components/Paginator';
import AlineaService from '~/Services/GES/AlineaService';

const COLUNAS = [
  { key: 'codigo',         title: 'Código',         ordenacao: true },
  { key: 'descricao',      title: 'Descrição',       ordenacao: true },
  { key: 'peso',           title: 'Peso',            ordenacao: true },
  { key: 'reapresentacao', title: 'Reapresentação',  ordenacao: true },
];

const TAMANHOS_PAGINA = [25, 50, 100];

const OPCOES_FILTRO = [
  { value: 'todos',     label: 'Todos' },
  { value: 'codigo',    label: 'Faixa de Código' },
  { value: 'descricao', label: 'Faixa de Descrição' },
];

const OPCOES_TAMANHO_PAGINA = TAMANHOS_PAGINA.map(t => ({ value: t, label: String(t) }));

const AlineaPage = () => {
  const [filtroTipo, setFiltroTipo]       = useState('todos');
  const [codigoIni, setCodigoIni]         = useState('');
  const [codigoFim, setCodigoFim]         = useState('');
  const [descricaoIni, setDescricaoIni]   = useState('');
  const [descricaoFim, setDescricaoFim]   = useState('');
  const [alineas, setAlineas]             = useState([]);
  const [loading, setLoading]             = useState(false);
  const [erro, setErro]                   = useState('');
  const [paginaAtual, setPaginaAtual]     = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(25);

  const limparCamposFiltro = () => {
    setCodigoIni('');
    setCodigoFim('');
    setDescricaoIni('');
    setDescricaoFim('');
  };

  const buscar = async () => {
    setErro('');
    setLoading(true);
    try {
      const params = {};

      if (filtroTipo === 'codigo') {
        // RN-04: Ini e Fim obrigatórios juntos
        if (!codigoIni || !codigoFim) {
          setErro('Informe Código Inicial e Final.');
          setLoading(false);
          return;
        }
        const ini = Number(codigoIni);
        const fim = Number(codigoFim);
        if (ini > fim) {
          setErro('Código Inicial não pode ser maior que Código Final.');
          setLoading(false);
          return;
        }
        params.codigoIni = ini;
        params.codigoFim = fim;
      } else if (filtroTipo === 'descricao') {
        const ini = (descricaoIni || '').trim();
        const fim = (descricaoFim || '').trim();
        // RN-04: Ini e Fim obrigatórios juntos
        if (!ini || !fim) {
          setErro('Informe Descrição Inicial e Final.');
          setLoading(false);
          return;
        }
        if (ini > fim) {
          setErro('Descrição Inicial não pode ser maior que Descrição Final.');
          setLoading(false);
          return;
        }
        params.descricaoIni = ini;
        params.descricaoFim = fim;
      }

      const resultado = await AlineaService.listar(params);
      setAlineas(resultado);
      setPaginaAtual(1);
      if (resultado.length === 0) setErro('Nenhum registro encontrado.');
    } catch (e) {
      const msg = e && e.message ? e.message : 'Erro ao buscar alíneas.';
      setErro(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroTipoChange = (opt) => {
    setFiltroTipo(opt ? opt.value : 'todos');
    limparCamposFiltro();
    setErro('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') buscar();
  };

  const handleExportarExcel = async () => {
    if (alineas.length === 0) {
      toast.warning('Nenhum dado para exportar.');
      return;
    }
    try {
      await AlineaService.exportarExcel(alineas);
    } catch {
      toast.error('Erro ao exportar para Excel.');
    }
  };

  useEffect(() => {
    buscar();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPaginas = Math.max(1, Math.ceil(alineas.length / itensPorPagina));

  const alineasPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    return alineas.slice(inicio, inicio + itensPorPagina);
  }, [alineas, paginaAtual, itensPorPagina]);

  const handleTamanhoPaginaChange = (opt) => {
    setItensPorPagina(opt ? opt.value : 25);
    setPaginaAtual(1);
  };

  const indiceInicial = alineas.length === 0 ? 0 : (paginaAtual - 1) * itensPorPagina + 1;
  const indiceFinal   = Math.min(paginaAtual * itensPorPagina, alineas.length);

  return (
    <Container fluid className="py-3">
      <Header title="Alíneas de Cheque" />

      <Card className="mt-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Filtrar por</Form.Label>
                <Select
                  options={OPCOES_FILTRO}
                  value={OPCOES_FILTRO.find(o => o.value === filtroTipo) || null}
                  onChange={handleFiltroTipoChange}
                  isClearable={false}
                />
              </Form.Group>
            </Col>

            {filtroTipo === 'codigo' && (
              <>
                <Col xs={6} md={2}>
                  <Form.Group>
                    <Form.Label>Código Inicial</Form.Label>
                    <Form.Control
                      type="number"
                      value={codigoIni}
                      onChange={e => setCodigoIni(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ex: 11"
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Group>
                    <Form.Label>Código Final</Form.Label>
                    <Form.Control
                      type="number"
                      value={codigoFim}
                      onChange={e => setCodigoFim(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ex: 20"
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            {filtroTipo === 'descricao' && (
              <>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label>Descrição Inicial</Form.Label>
                    <Form.Control
                      type="text"
                      value={descricaoIni}
                      onChange={e => setDescricaoIni(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder='Ex: "A"'
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label>Descrição Final</Form.Label>
                    <Form.Control
                      type="text"
                      value={descricaoFim}
                      onChange={e => setDescricaoFim(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder='Ex: "M"'
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col xs={12} md="auto">
              <Form.Group>
                <Form.Label className="d-none d-md-block">&nbsp;</Form.Label>
                <div>
                  <Button variant="primary" onClick={buscar} className="mr-2">
                    <FaSearch className="mr-1" /> Filtrar
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleExportarExcel}
                    disabled={alineas.length === 0}
                  >
                    <FaFileExcel className="mr-1" /> Exportar Excel
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && <Loading label="Carregando alíneas..." />}

      {!loading && erro && (
        <div className="alert alert-warning mt-3">{erro}</div>
      )}

      {!loading && alineas.length > 0 && (
        <div className="mt-3">
          <Tabela colunas={COLUNAS} dados={alineasPagina} />

          <Row className="align-items-center mt-3">
            <Col xs={12} md={4} className="mb-2 mb-md-0">
              <Form.Group className="mb-0 d-flex align-items-center">
                <Form.Label className="mb-0 mr-2">Itens por página:</Form.Label>
                <Select
                  options={OPCOES_TAMANHO_PAGINA}
                  value={OPCOES_TAMANHO_PAGINA.find(o => o.value === itensPorPagina) || null}
                  onChange={handleTamanhoPaginaChange}
                  isClearable={false}
                  styles={{ container: base => ({ ...base, width: 100 }) }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="d-flex justify-content-center mb-2 mb-md-0">
              {totalPaginas > 1 && (
                <Paginator
                  numberOfPages={totalPaginas}
                  initialPage={paginaAtual}
                  onPageChange={setPaginaAtual}
                />
              )}
            </Col>
            <Col xs={12} md={4} className="text-md-right">
              <small className="text-muted">
                Exibindo {indiceInicial}-{indiceFinal} de {alineas.length}
              </small>
            </Col>
          </Row>
        </div>
      )}
    </Container>
  );
};

export default AlineaPage;
